// src/components/SignUpModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  auth,
  db,
  signInWithPhone,
  verifyPhoneCode,
  functions,
} from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import { RecaptchaVerifier } from "firebase/auth";
import { generateMemberId } from "@/lib/memberId";
import {
  handlePhoneInputChange,
  normalizePhoneNumber,
  isValidPhoneNumber,
} from "@/lib/phoneUtils";
import { ErrorCard } from "./NotificationCard";
import { X } from "lucide-react";

/* ---------------- Modal portal ---------------- */
function ModalRoot({ children }) {
  const [el] = useState(() => document.createElement("div"));
  useEffect(() => {
    el.setAttribute("id", "modal-root");
    document.body.appendChild(el);
    return () => document.body.removeChild(el);
  }, [el]);
  return createPortal(children, el);
}

/* ---------------- Invisible reCAPTCHA (singleton) ---------------- */
// We keep ONE invisible v2 verifier and reuse it across flows.
function ensureInvisibleRecaptcha() {
  if (window.__recaptcha) return window.__recaptcha;

  // Create off-screen container; do NOT hide via display:none/visibility:hidden
  let box = document.getElementById("recaptcha-isolated-container");
  if (!box) {
    box = document.createElement("div");
    box.id = "recaptcha-isolated-container";
    box.style.position = "fixed";
    box.style.left = "-9999px";
    box.style.top = "0";
    box.style.width = "1px";
    box.style.height = "1px";
    box.style.zIndex = "2147483647";
    document.body.appendChild(box);
  }

  const verifier = new RecaptchaVerifier(auth, "recaptcha-isolated-container", {
    size: "invisible",
    callback: (token) => {},
    "expired-callback": () => console.warn("⚠️ reCAPTCHA expired"),
    "error-callback": (e) => console.error("❌ reCAPTCHA error", e),
  });

  verifier.render().then((id) => console.log("reCAPTCHA widget id:", id));
  window.__recaptcha = verifier;
  return verifier;
}
function resetInvisibleRecaptcha() {
  try {
    if (window.__recaptcha) {
      window.__recaptcha.clear();
      window.__recaptcha = null;
    }
    const node = document.getElementById("recaptcha-isolated-container");
    if (node) node.remove();
  } catch (e) {
    console.warn("recaptcha reset error", e);
  }
}

/* ---------------- Component ---------------- */
export default function SignUpModal({ isOpen, onClose, onSignUpSuccess }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  // Tabs
  const [isLoginMode, setIsLoginMode] = useState(false);

  // Login (email/pass)
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [lastSubmit, setLastSubmit] = useState(0);
  const [loginPhoneOpen, setLoginPhoneOpen] = useState(false);

  // Phone (shared)
  const [phoneNumber, setPhoneNumber] = useState("+63");
  const [verificationId, setVerificationId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  // Signup fields
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");

  const modalOpenRef = useRef(false);

  /* ------------- Modal open/close effects ------------- */
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
      modalOpenRef.current = true;
      // Pre-create the singleton (no-op if already present)
      ensureInvisibleRecaptcha();
    } else {
      document.body.classList.remove("modal-open");
      modalOpenRef.current = false;
      // keep verifier for future unless you prefer resetting:
      // resetInvisibleRecaptcha();
    }
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isOpen]);

  /* ------------- Helpers ------------- */
  const validateSignupData = async (email, phone) => {
    try {
      const validateData = httpsCallable(functions, "validateSignupData");
      const res = await validateData({ email, phone });
      return res.data;
    } catch (e) {
      console.error("validateSignupData error:", e);
      return { isValid: false, errors: ["Validation failed. Please try again."] };
    }
  };

  const handlePhoneNumberChange = (e) => {
    const formatted = handlePhoneInputChange(e.target.value, true);
    setPhoneNumber(formatted);
    if (phoneError) setPhoneError("");
  };

  const isFormValid = () =>
    firstName.trim() &&
    lastName.trim() &&
    address.trim() &&
    isValidPhoneNumber(phoneNumber) &&
    !!window.__recaptcha;

  useEffect(() => {
    const t = setTimeout(async () => {
      if (phoneNumber && phoneNumber !== "+63" && isValidPhoneNumber(phoneNumber)) {
        const normalized = normalizePhoneNumber(phoneNumber);
        const validation = await validateSignupData(null, normalized);
        if (!validation.isValid) {
          validation.errors.forEach((msg) => {
            if (msg.includes("Phone")) setPhoneError(msg);
          });
        }
      }
    }, 500);
    return () => clearTimeout(t);
  }, [phoneNumber]);

  function looksLikeEmail(v) {
    return v.includes("@");
  }

  async function resolveEmailFromMemberId(mid) {
    const id = String(mid).trim();
    if (!id) throw new Error("Enter your Member ID or Email");
    const snap = await getDoc(doc(db, "memberLookup", id));
    if (!snap.exists()) throw new Error("Member ID not found. Use your email or contact support.");
    const data = snap.data() || {};
    if (!data.email) throw new Error("No email linked to this Member ID.");
    return data.email;
  }

  /* ------------- LOGIN: email + password ------------- */
  async function onSubmit(e) {
    e.preventDefault();
    if (busy || (lockoutUntil && Date.now() < lockoutUntil)) return;
    if (Date.now() - lastSubmit < 1000) return;
    setLastSubmit(Date.now());
    setErr("");
    setBusy(true);

    try {
      const id = identifier.trim();
      const pass = password.trim();
      const email = looksLikeEmail(id) ? id : await resolveEmailFromMemberId(id);

      await signInWithEmailAndPassword(auth, email, pass);
      await postLoginSuccess();
    } catch (e2) {
      setFailCount((f) => f + 1);
      if (failCount + 1 >= 5) {
        setLockoutUntil(Date.now() + 30_000);
        setErr("Too many failed attempts. Please wait 30 seconds.");
      } else if (e2.code === "auth/wrong-password" || e2.code === "auth/invalid-credential") {
        setErr(
          "Invalid credentials or password. Please try again.\n\n💡 If you signed up with Phone, set a password in Profile → Manage Password."
        );
      } else {
        setErr("Invalid credentials or password. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function postLoginSuccess() {
    // optional: update lastLoginAt
    try {
      const uid = auth.currentUser?.uid;
      if (uid) await updateDoc(doc(db, "users", uid), { lastLoginAt: serverTimestamp() });
    } catch {}
    // reset
    setIdentifier("");
    setPassword("");
    setFailCount(0);
    setPhoneNumber("+63");
    setVerificationId("");
    setVerificationCode("");
    onSignUpSuccess?.(auth.currentUser || null);
    onClose?.();
  }

  /* ------------- SIGNUP: send code ------------- */
  const handlePhoneSignUp = async () => {
    try {
      setBusy(true);
      setErr("");
      setPhoneError("");
      setEmailError("");

      const normalized = normalizePhoneNumber(phoneNumber);
      const validation = await validateSignupData(null, normalized);
      if (!validation.isValid) {
        validation.errors.forEach((m) => {
          if (m.includes("Phone")) setPhoneError(m);
          if (m.includes("Email")) setEmailError(m);
        });
        return;
      }

      const verifier = window.__recaptcha || ensureInvisibleRecaptcha();
      const vid = await signInWithPhone(normalized, verifier);
      setVerificationId(vid);
    } catch (error) {
      console.error("Phone sign-up error:", error);
      if (error.code === "auth/too-many-requests") setErr("Too many attempts. Please try again later.");
      else if (error.code === "auth/invalid-phone-number") setErr("Invalid phone number format. Please check and try again.");
      else if (error.code === "auth/quota-exceeded") setErr("SMS quota exceeded. Please try again later.");
      else if (error.code === "auth/captcha-check-failed") setErr("reCAPTCHA verification failed. Please try again.");
      else setErr(error.message || "Failed to send verification code. Please try again.");
      try { await (window.__recaptcha?.render?.()); } catch {}
    } finally {
      setBusy(false);
    }
  };

  /* ------------- SIGNUP: verify code & create profile ------------- */
  const handlePhoneVerification = async () => {
    try {
      setBusy(true);
      setErr("");

      const result = await verifyPhoneCode(verificationId, verificationCode);
      const user = result.user;

      const profileRef = doc(db, "users", user.uid);
      const profileSnap = await getDoc(profileRef);

      if (!profileSnap.exists()) {
        const memberId = await generateMemberId(db);
        const base = {
          uid: user.uid,
          firstName: firstName.trim(),
          middleName: middleName.trim(),
          lastName: lastName.trim(),
          address: address.trim(),
          phone: user.phoneNumber,
          phoneNumber: user.phoneNumber,
          email: user.email || "",
          createdAt: serverTimestamp(),
          role: "member",
          status: "active",
          memberId,
          profileComplete: false,
        };
        await setDoc(profileRef, { ...base, authProvider: "phone", phoneVerified: true });
        await setDoc(doc(db, "members", user.uid), {
          uid: user.uid,
          firstName: base.firstName,
          middleName: base.middleName,
          lastName: base.lastName,
          address: base.address,
          phone: base.phone,
          phoneNumber: base.phoneNumber,
          createdAt: serverTimestamp(),
          memberId,
          status: "active",
          profileComplete: false,
        });
        await setDoc(doc(db, "memberLookup", memberId), {
          uid: user.uid,
          phoneNumber: user.phoneNumber,
        });
      }

      // tiny delay for writes to propagate
      await new Promise((r) => setTimeout(r, 500));

      onSignUpSuccess?.(user);
      onClose?.();
    } catch (error) {
      console.error("Phone verification error:", error);
      setErr(error.message || "Failed to verify code");
    } finally {
      setBusy(false);
    }
  };

  /* ------------- LOGIN: phone send & verify ------------- */
  const handlePhoneSignIn = async () => {
    try {
      setBusy(true);
      setErr("");

      const normalized = normalizePhoneNumber(phoneNumber);
      if (!isValidPhoneNumber(normalized)) {
        setErr("Please enter a valid PH mobile number (e.g., +63 9XX-XXX-XXXX).");
        return;
      }

      const verifier = window.__recaptcha || ensureInvisibleRecaptcha();
      const vid = await signInWithPhone(normalized, verifier);
      setVerificationId(vid);
    } catch (error) {
      console.error("Phone sign-in error:", error);
      if (error.code === "auth/too-many-requests") setErr("Too many attempts. Please try again later.");
      else if (error.code === "auth/invalid-phone-number") setErr("Invalid phone number format. Please check and try again.");
      else if (error.code === "auth/quota-exceeded") setErr("SMS quota exceeded. Please try again later.");
      else if (error.code === "auth/captcha-check-failed") setErr("reCAPTCHA verification failed. Please try again.");
      else setErr(error.message || "Failed to send verification code. Please try again.");
      try { await (window.__recaptcha?.render?.()); } catch {}
    } finally {
      setBusy(false);
    }
  };

  const handlePhoneVerificationLogin = async () => {
    try {
      setBusy(true);
      setErr("");
      const result = await verifyPhoneCode(verificationId, verificationCode);
      const user = result.user;

      const profileRef = doc(db, "users", user.uid);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) {
        const memberId = await generateMemberId(db);
        await setDoc(profileRef, {
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          createdAt: serverTimestamp(),
          authProvider: "phone",
          phoneVerified: true,
          role: "member",
          status: "active",
          memberId,
        });
      }

      await postLoginSuccess();
    } catch (error) {
      console.error("Phone verification error:", error);
      setErr(error.message || "Failed to verify code");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Google Sign-In (server-side redirect) ----------
  const handleGoogleSignIn = () => {
    try {
      setBusy(true);
      setErr("");
      const next = encodeURIComponent(window.location.pathname + window.location.search || "/");
      window.location.href = `/api/auth/google/login?next=${next}`;
    } catch (error) {
      console.error("Google sign-in redirect error:", error);
      setErr(error.message || "Google sign-in failed. Please try again.");
      setBusy(false);
    }
  };

  // ---------- Resend ----------
  const handleResendCode = async () => {
    try {
      setVerificationId("");
      setVerificationCode("");
      setErr("");
      ensureInvisibleRecaptcha();
    } catch (e) {
      console.error("Failed to reset reCAPTCHA:", e);
      setErr("Failed to reset verification. Please refresh the page.");
    }
  };

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setFirstName("");
      setMiddleName("");
      setLastName("");
      setAddress("");
      setPhoneNumber("+63");
      setVerificationId("");
      setVerificationCode("");
      setErr("");
      setPassword("");
      setIsLoginMode(false);
      setIdentifier("");
      setFailCount(0);
      setLockoutUntil(null);
      setLastSubmit(0);
      setLoginPhoneOpen(false);
      // keep the singleton around for next open (faster UX)
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalRoot>
      <div className="fixed inset-0 z-[9998] bg-black/80" aria-hidden="true" />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{isLoginMode ? "Sign in" : "Create Your Account"}</h2>
                <p className="text-sm text-gray-600">{isLoginMode ? "Access your account" : "Get instant access to our stores and checkout"}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex mt-4 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setIsLoginMode(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !isLoginMode ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Sign Up
              </button>
              <button
                onClick={() => setIsLoginMode(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  isLoginMode ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Sign In
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            {/* LOGIN TAB */}
            {isLoginMode && (
              <div className="space-y-4">
                <form onSubmit={onSubmit} className="grid gap-3" autoComplete="off">
                  <label className="block">
                    <span className="text-sm font-semibold text-gray-700 mb-2 block">Email or Member ID</span>
                    <input
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value.trimStart())}
                      autoComplete="username"
                      placeholder="you@example.com or 202500001"
                      required
                      disabled={busy || (lockoutUntil && Date.now() < lockoutUntil)}
                    />
                  </label>

                  <label className="block relative">
                    <span className="text-sm font-semibold text-gray-700 mb-2 block">Password</span>
                    <input
                      className="w-full px-3 sm:px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="off"
                      required
                      disabled={busy || (lockoutUntil && Date.now() < lockoutUntil)}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-8 text-xs text-gray-500 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </label>

                  <button
                    type="submit"
                    disabled={busy || (lockoutUntil && Date.now() < lockoutUntil)}
                    className="w-full btn btn-primary py-3 px-4 text-base font-semibold disabled:opacity-60 mt-1"
                  >
                    {lockoutUntil && Date.now() < lockoutUntil
                      ? `Locked (${Math.ceil((lockoutUntil - Date.now()) / 1000)}s)`
                      : busy
                      ? "Signing in…"
                      : "Sign in"}
                  </button>
                </form>

                {/* Divider */}
                <div className="mt-6">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500">or</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                </div>

                {/* Google Sign-In */}
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={busy || (lockoutUntil && Date.now() < lockoutUntil)}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 font-medium text-sm text-gray-700 shadow-sm transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                </div>

                {/* Phone login */}
                <div className="mt-4 space-y-2.5">
                  {/* Toggle open/close */}
                  <button
                    type="button"
                    onClick={() => {
                      setLoginPhoneOpen((v) => !v);
                      // ensure reCAPTCHA is ready when opening the phone section
                      if (!loginPhoneOpen) {
                        ensureInvisibleRecaptcha();
                      }
                    }}
                    disabled={busy}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors font-medium text-sm"
                  >
                    📱 {loginPhoneOpen ? "Hide Phone Sign-in" : "Continue with Phone"}
                  </button>

                  {/* Phone input step */}
                  {loginPhoneOpen && !verificationId && (
                    <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2.5">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                        placeholder="+63 950-468-6668"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        maxLength={17}
                        required
                      />
                      <div className="text-xs text-gray-500">Format: +63 9XX-XXX-XXXX</div>

                      <button
                        type="button"
                        onClick={handlePhoneSignIn}
                        disabled={busy || !phoneNumber}
                        className="w-full btn btn-primary py-3 px-4 text-base font-semibold"
                      >
                        {busy ? "Sending code..." : "Send Verification Code"}
                      </button>
                    </div>
                  )}

                  {/* Verification code step */}
                  {loginPhoneOpen && verificationId && (
                    <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="space-y-2.5">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Verification Code</label>
                        <input
                          type="text"
                          className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                          placeholder="Enter 6-digit code"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          maxLength={6}
                          required
                        />
                        <button
                          type="button"
                          onClick={handlePhoneVerificationLogin}
                          disabled={busy || !verificationCode}
                          className="w-full btn btn-success py-3 px-4 text-base font-semibold"
                        >
                          {busy ? "Verifying..." : "Verify Code"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3 text-xs">
                    <a href="/reset" className="underline text-blue-600 hover:text-blue-800">
                      Forgot password?
                    </a>
                    <button
                      type="button"
                      onClick={() => setIsLoginMode(false)}
                      className="underline text-blue-600 hover:text-blue-800"
                    >
                      No account? Sign up
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* SIGNUP TAB */}
            {!isLoginMode && (
              <div className="space-y-4">
                {!verificationId ? (
                  <div className="space-y-4">
                    {/* Names */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Middle Name</label>
                        <input
                          type="text"
                          className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                          value={middleName}
                          onChange={(e) => setMiddleName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                      <textarea
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        rows="3"
                        required
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        className={`w-full px-3 sm:px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base ${
                          phoneError ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                        placeholder="+63 950-468-6668"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        maxLength={17}
                        required
                      />
                      {phoneError ? (
                        <div className="text-sm text-red-600 mt-1 px-1">{phoneError}</div>
                      ) : (
                        <div className="text-xs text-gray-500 mt-1 px-1">Format: +63 950-468-6668</div>
                      )}
                    </div>

                    <div className="text-center">
                      <div className="min-h-[48px] flex items-center justify-center rounded-lg">
                        <span className="text-sm text-gray-500">Security verification will run automatically.</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="mt-6">
                      <div className="flex items-center">
                        <div className="flex-1 border-t border-gray-300"></div>
                        <span className="px-3 text-sm text-gray-500">or</span>
                        <div className="flex-1 border-t border-gray-300"></div>
                      </div>
                    </div>

                    {/* Google Sign-In */}
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={busy}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 font-medium text-sm text-gray-700 shadow-sm transition-colors"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handlePhoneSignUp}
                      disabled={busy || !isFormValid()}
                      className="w-full btn btn-primary py-3 px-4 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? "Sending code..." : "Send Verification Code"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Code Sent!</h3>
                      <p className="text-gray-600 text-sm">We’ve sent a 6-digit code to {phoneNumber}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Verification Code</label>
                      <input
                        type="text"
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        maxLength={6}
                        required
                        autoFocus
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handlePhoneVerification}
                      disabled={busy || !verificationCode || verificationCode.length !== 6}
                      className="w-full btn btn-success py-3 px-4 text-base font-semibold"
                    >
                      {busy ? "Verifying..." : "Complete Signup & Continue Order"}
                    </button>

                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={busy}
                      className="w-full text-gray-600 py-2 px-4 rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Resend Code
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Errors */}
            {err && (
              <ErrorCard className="mt-4">
                <div className="text-sm whitespace-pre-line">{err}</div>
              </ErrorCard>
            )}
          </div>
        </div>
      </div>

      {/* tiny spinner style */}
      <style>{`.loader{border:2px solid #e5e7eb;border-top:2px solid #2563eb;border-radius:50%;width:1em;height:1em;animation:spin .8s linear infinite;display:inline-block;vertical-align:middle;}@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </ModalRoot>
  );
}
